// Helpers for resolving line-item-label ids across the native (UUID) and legacy
// (Placements.IO) keys. Native line-item FK reads are permanently on since the
// PIO cutover; the legacy key is kept as a dual-read fallback until the
// placements_line_item_id columns are dropped (MD-4994 / M3-102).
export type LineItemLabelLineItemRecord = {
  id?: string | number | null;
  externalPioId?: string | null;
  placementsLineItemId?: string | number | null;
  lineItemId?: string | null;
};

export type LineItemLabelLookupIds = {
  placementsLineItemId?: string;
  nativeLineItemId?: string;
};

export type LineItemLabelCreateData = {
  placementsLineItemId: string;
  labelId: string;
  lineItemId?: string;
};

const nativeUuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const presentString = (value: string | number | null | undefined) => {
  if (value === null || value === undefined) return undefined;

  const stringValue = String(value);
  return stringValue.length > 0 ? stringValue : undefined;
};

export const isNativeLineItemId = (value: string | number | null | undefined) => {
  const stringValue = presentString(value);
  return Boolean(stringValue && nativeUuidPattern.test(stringValue));
};

export const getLineItemLabelLookupIds = (
  lineItem: LineItemLabelLineItemRecord | string | number | null | undefined,
): LineItemLabelLookupIds => {
  if (typeof lineItem === 'string' || typeof lineItem === 'number') {
    const lineItemId = presentString(lineItem);

    return {
      placementsLineItemId: lineItemId,
      nativeLineItemId: isNativeLineItemId(lineItemId) ? lineItemId : undefined,
    };
  }

  const recordId = presentString(lineItem?.id);
  const nativeLineItemId = presentString(lineItem?.lineItemId) ?? (isNativeLineItemId(recordId) ? recordId : undefined);

  return {
    placementsLineItemId:
      presentString(lineItem?.placementsLineItemId) ?? presentString(lineItem?.externalPioId) ?? recordId,
    nativeLineItemId,
  };
};

export const buildLineItemLabelCreateData = ({
  lineItem,
  labelId,
}: {
  lineItem: LineItemLabelLineItemRecord | string | number | null | undefined;
  labelId: string;
}): LineItemLabelCreateData | undefined => {
  const ids = getLineItemLabelLookupIds(lineItem);

  if (!ids.placementsLineItemId) return undefined;

  return {
    placementsLineItemId: ids.placementsLineItemId,
    labelId,
    ...(ids.nativeLineItemId ? { lineItemId: ids.nativeLineItemId } : {}),
  };
};

// MD-5145: a native creative persists only line_item_id (placements_line_item_id is
// NULL). The creative edit forms bind the required line-item SelectInput to
// `placementsLineItemId`, so on EDIT load we normalize that field from the native
// lineItemId — otherwise it opens blank and the form can't be saved. Imported
// records already carry placementsLineItemId, so this is a no-op for them.
export const withPlacementsLineItemIdFromNativeForEdit = <
  T extends { placementsLineItemId?: string | number | null; lineItemId?: string | null },
>(
  record: T,
): T => {
  if (presentString(record.placementsLineItemId)) return record;
  const nativeLineItemId = presentString(record.lineItemId);
  return nativeLineItemId ? { ...record, placementsLineItemId: nativeLineItemId } : record;
};

// MD-5145: resolve the parent line-item Show path for a creative create/edit
// onSuccess redirect, reading from whichever source records carry the ids (form
// data, previousData, mutation response) — so it does NOT depend on a mutation
// response selection set (which for clicks/sct only returns `id`).
//
// The Placements.IO API shut off at contract end, so the legacy
// `/line_items/:id/show` page can no longer load — every redirect must land on
// the native `/lineItems/:uuid/show` route. The line-item SelectInput binds to
// `placementsLineItemId`, so that field carries the user's current selection;
// `lineItemId` is the pre-save native FK, which is stale when the save moved
// the creative to a different line item. Prefer the selection (translating a
// legacy id to its native UUID via externalPioId — imported line items exist
// natively) and fall back to lineItemId only when no selection is present or
// the translation misses.
type CreativeLineItemRefRecord = { placementsLineItemId?: string | number | null; lineItemId?: string | null };

const firstPresentRef = (
  records: Array<CreativeLineItemRefRecord | null | undefined>,
  key: keyof CreativeLineItemRefRecord,
) => {
  for (const record of records) {
    const value = presentString(record?.[key]);
    if (value) return value;
  }
  return undefined;
};

export const creativeParentLineItemShowPath = (
  ...records: Array<CreativeLineItemRefRecord | null | undefined>
): string | undefined => {
  const selectedId = firstPresentRef(records, 'placementsLineItemId');
  if (isNativeLineItemId(selectedId)) return `/lineItems/${selectedId}/show`;
  // A legacy selection must not short-circuit to a (possibly stale) lineItemId;
  // it needs the async externalPioId translation below.
  if (selectedId) return undefined;

  const nativeId = firstPresentRef(records, 'lineItemId');
  return nativeId ? `/lineItems/${nativeId}/show` : undefined;
};

type LineItemListLookup = {
  getList: (
    resource: string,
    params: {
      filter: Record<string, unknown>;
      pagination: { page: number; perPage: number };
      sort: { field: string; order: 'ASC' | 'DESC' };
    },
  ) => Promise<{ data?: Array<{ id?: string | number }> }>;
};

export const resolveCreativeParentLineItemShowPath = async (
  dataProvider: LineItemListLookup,
  ...records: Array<CreativeLineItemRefRecord | null | undefined>
): Promise<string | undefined> => {
  const nativePath = creativeParentLineItemShowPath(...records);
  if (nativePath) return nativePath;

  const legacyId = firstPresentRef(records, 'placementsLineItemId');
  if (!legacyId) return undefined;

  // A translation miss/failure means the stored selection is orphaned (no move
  // happened), so the record's own lineItemId — when present — is still the
  // right parent to land on.
  const fallbackNativeId = firstPresentRef(records, 'lineItemId');
  const fallbackPath = fallbackNativeId ? `/lineItems/${fallbackNativeId}/show` : undefined;

  try {
    // Explicit equalTo: ra-postgraphile maps a plain string filter value to the
    // substring `includes` operator, which could match the wrong line item.
    const { data } = await dataProvider.getList('lineItems', {
      filter: { externalPioId: { operator: 'equalTo', value: legacyId }, includeArchived: true },
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'id', order: 'ASC' },
    });
    const nativeId = presentString(data?.[0]?.id);
    return nativeId ? `/lineItems/${nativeId}/show` : fallbackPath;
  } catch {
    return fallbackPath;
  }
};
